'use client';

import classNames from 'classnames';
import HTMLReactParser from 'html-react-parser';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { Button } from '@get-mosh/msh-shared';
import FormulaCard, { FormulaCardProps } from '../FormulaCard';
import SnakePager from '../SnakePager';
import styles from './dynamic-footer-banner.module.scss';
import { EventProps } from 'src/analytics/EventProps';
import { trackLandingPageButtonClick } from 'src/analytics/Events';
import Link from 'next/link';

export type DynamicFooterBannerProps = {
    className?: string;
    header: string;
    cta: {
        label: string;
        link: string;
        eventProps?: EventProps;
    };
    slides: Array<{
        image: {
            src: string;
            alt: string;
        };
        formula: FormulaCardProps;
    }>;
};

const SLIDE_DURATION = 4000;

const DynamicFooterBanner = ({
    className,
    header,
    cta,
    slides,
}: DynamicFooterBannerProps) => {
    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((currentSlide + 1) % slides.length);
        }, SLIDE_DURATION);
        return () => clearInterval(interval);
    }, [currentSlide]);

    const onClick = () => {
        trackLandingPageButtonClick({
            location: 'Footer',
            label: cta.label,
            ...cta.eventProps,
        });
    };

    return (
        <div className={classNames(styles['wrapper'], className)}>
            <div>
                <h2>{HTMLReactParser(header)}</h2>
                <Button
                    text={cta.label}
                    as={Link}
                    href={cta.link}
                    onClick={onClick}
                    className={styles['call-to-action']}
                    stretch
                />
            </div>
            <div className={styles['content-container']}>
                {slides.map((slide, index) => (
                    <div
                        key={index}
                        className={styles['slide']}
                        data-active={index === currentSlide}
                    >
                        <div className={styles['image-container']}>
                            <Image
                                src={slide.image.src}
                                alt={slide.image.alt}
                                fill
                                sizes="(max-width: 768px) 100vw, 50vw"
                            />
                        </div>
                        <FormulaCard
                            className={styles['formula']}
                            {...slide.formula}
                        />
                    </div>
                ))}
                <SnakePager
                    className={styles['pager']}
                    page={currentSlide}
                    pages={slides.length}
                    timer={SLIDE_DURATION}
                />
            </div>
        </div>
    );
};

export default DynamicFooterBanner;
